import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { parse as parseDate, parseISO } from 'date-fns';
import { ArrowLeft, FileUp, Play, RefreshCw } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { usePinAuth } from '@/hooks/usePinAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

type ProjectRow = {
  id?: string;
  name: string;
  description?: string | null;
  address?: string | null;
  status?: string | null;
};

type ProfileRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

type ProjectFlowRow = {
  id_projet?: string;
  nom_du_projet?: string;
  statut?: string;
  adresse_chantier?: string;
  adresse_specifique?: string;
  numero_de_client?: string;
  heures_planiifiees?: string;
};

type EmployeeRegistryRow = {
  nom_complet?: string;
  courriel?: string;
  etat?: string;
};

type TimesheetRow = {
  date?: string;
  employe?: string;
  projet?: string;
  heure_debut?: string;
  heure_fin?: string;
  tache_principale?: string;
  details?: string;
  facturable?: string;
  qbe?: string;
  taux_horaire?: string;
  statut_de_shift?: string;
};

type UnknownValue = { value: string; count: number; mappedId?: string };

function stripDiacritics(input: string) {
  return input.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeKey(input: string) {
  return stripDiacritics(String(input ?? ''))
    .trim()
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeValue(input: string) {
  return stripDiacritics(String(input ?? ''))
    .trim()
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/\s+/g, ' ');
}

function parseBooleanLike(v: unknown): boolean | null {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (!s) return null;
  if (['true', 'vrai', '1', 'yes', 'y', 'oui'].includes(s)) return true;
  if (['false', 'faux', '0', 'no', 'n', 'non'].includes(s)) return false;
  return null;
}

function parseNumberComma(v: unknown): number | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const normalized = s.replace(/\s/g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function parseDateParts(dateStr: string): { year: number; month: number; day: number } | null {
  const s = String(dateStr ?? '').trim();
  if (!s) return null;

  // IMPORTANT: ne pas utiliser parseISO("YYYY-MM-DD") ici.
  // Dans certains fuseaux, ça peut créer une date "UTC minuit" et décaler le jour en local.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map((x) => Number(x));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (m < 1 || m > 12) return null;
    if (d < 1 || d > 31) return null;
    return { year: y, month: m, day: d };
  }

  // dd/mm/yyyy (ou d/m/yyyy)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    const [dStr, mStr, yStr] = s.split('/');
    const d = Number(dStr);
    const m = Number(mStr);
    const y = Number(yStr);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (m < 1 || m > 12) return null;
    if (d < 1 || d > 31) return null;
    return { year: y, month: m, day: d };
  }

  return null;
}

function buildDateTimeLocal(dateStr: string, timeStr: string): Date | null {
  const parts = parseDateParts(dateStr);
  if (!parts) return null;

  const t = String(timeStr ?? '').trim();
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
  if (!m) return null;

  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const ss = Number(m[3] ?? '0');
  if (![hh, mm, ss].every((n) => Number.isFinite(n))) return null;
  if (hh < 0 || hh > 23) return null;
  if (mm < 0 || mm > 59) return null;
  if (ss < 0 || ss > 59) return null;

  const local = new Date(parts.year, parts.month - 1, parts.day, hh, mm, ss, 0);
  return Number.isNaN(local.getTime()) ? null : local;
}

function extractPfIdFromDescription(desc: string | null | undefined): string | null {
  if (!desc) return null;
  const m = /(?:^|\s)pf_id=([a-zA-Z0-9_-]+)(?:\s|$)/.exec(desc);
  return m?.[1] ?? null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function getInsertedCountFromRpc(data: unknown): number {
  if (Array.isArray(data) && data.length > 0 && isRecord(data[0]) && typeof data[0].inserted_count === 'number') {
    return data[0].inserted_count;
  }
  if (isRecord(data) && typeof data.inserted_count === 'number') {
    return data.inserted_count;
  }
  return 0;
}

function getErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  if (isRecord(e) && typeof e.message === 'string') return e.message;
  return 'Erreur inconnue';
}

async function parseCsvFile<T extends Record<string, unknown>>(file: File): Promise<T[]> {
  const text = await file.text();
  const { data, errors } = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => normalizeKey(h),
  });
  if (errors?.length) throw new Error(`Erreur de parsing CSV: ${errors[0]?.message ?? 'inconnue'}`);
  return (Array.isArray(data) ? data : []) as T[];
}

function mapProjectStatus(raw: string | null | undefined): 'active' | 'completed' | 'on-hold' {
  const s = normalizeValue(raw ?? '');
  if (s.includes('termin')) return 'completed';
  if (s.includes('pause') || s.includes('hold')) return 'on-hold';
  return 'active';
}

type BuiltMaps = {
  existingByName: Map<string, string>;
  existingByPfId: Map<string, string>;
  pfById: Map<string, ProjectFlowRow>;
  pfByName: Map<string, ProjectFlowRow>;
  profileByEmail: Map<string, string>;
  profileByFullName: Map<string, string>;
  registryByFullNameToEmail: Map<string, string>;
};

function buildMaps(
  existingProjects: ProjectRow[],
  projectFlowRows: ProjectFlowRow[],
  profiles: ProfileRow[],
  employeeRegistryRows: EmployeeRegistryRow[],
): BuiltMaps {
  const existingByName = new Map<string, string>();
  const existingByPfId = new Map<string, string>();

  for (const p of existingProjects) {
    if (!p.id) continue;
    const nameKey = normalizeValue(p.name);
    if (nameKey) existingByName.set(nameKey, p.id);
    const pfId = extractPfIdFromDescription(p.description);
    if (pfId) existingByPfId.set(normalizeValue(pfId), p.id);
  }

  const pfById = new Map<string, ProjectFlowRow>();
  const pfByName = new Map<string, ProjectFlowRow>();
  for (const r of projectFlowRows) {
    const idProjet = normalizeValue(r.id_projet ?? '');
    const name = normalizeValue(r.nom_du_projet ?? '');
    if (idProjet) pfById.set(idProjet, r);
    if (name) pfByName.set(name, r);
  }

  const profileByEmail = new Map<string, string>();
  const profileByFullName = new Map<string, string>();
  for (const p of profiles) {
    if (p.email) profileByEmail.set(normalizeValue(p.email), p.id);
    const full = normalizeValue(`${p.first_name ?? ''} ${p.last_name ?? ''}`.trim());
    if (full) profileByFullName.set(full, p.id);
  }

  const registryByFullNameToEmail = new Map<string, string>();
  for (const r of employeeRegistryRows) {
    const full = normalizeValue(r.nom_complet ?? '');
    const email = normalizeValue(r.courriel ?? '');
    if (full && email) registryByFullNameToEmail.set(full, email);
  }

  return {
    existingByName,
    existingByPfId,
    pfById,
    pfByName,
    profileByEmail,
    profileByFullName,
    registryByFullNameToEmail,
  };
}

export default function AdminImportProjetsShifts() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pinUser, loading: pinLoading, isManager: isPinManager, getManagerPin } = usePinAuth();

  const [timesheetFile, setTimesheetFile] = useState<File | null>(null);
  const [projectFlowFile, setProjectFlowFile] = useState<File | null>(null);
  const [employeeRegistryFile, setEmployeeRegistryFile] = useState<File | null>(null);

  const [loading, setLoading] = useState(false);
  const [analysisDone, setAnalysisDone] = useState(false);

  const [existingProjects, setExistingProjects] = useState<ProjectRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);

  const [projectFlowRows, setProjectFlowRows] = useState<ProjectFlowRow[]>([]);
  const [employeeRegistryRows, setEmployeeRegistryRows] = useState<EmployeeRegistryRow[]>([]);
  const [timesheetRows, setTimesheetRows] = useState<TimesheetRow[]>([]);

  const [missingProjects, setMissingProjects] = useState<ProjectFlowRow[]>([]);
  const [conflictingProjects, setConflictingProjects] = useState<ProjectFlowRow[]>([]);
  const [unknownProjectValues, setUnknownProjectValues] = useState<UnknownValue[]>([]);
  const [unknownEmployeeValues, setUnknownEmployeeValues] = useState<UnknownValue[]>([]);

  const [skipUnmapped, setSkipUnmapped] = useState(true);

  const getImportPinOrThrow = () => {
    const pin = getManagerPin();
    if (!pin) throw new Error('PIN manager manquant. Reconnecte-toi via PIN.');
    return pin;
  };

  const shouldUsePinMode = () => {
    // IMPORTANT: en mode PIN, on n'a pas de JWT Supabase => PostgREST peut retourner 401.
    // On force donc les RPC PIN dès que l'app est en auth_method=pin.
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('auth_method') === 'pin';
  };

  const isUnauthorized = (e: unknown) => {
    if (!e || typeof e !== 'object') return false;
    const status = (e as { status?: number }).status;
    const code = (e as { code?: string }).code;
    const message = (e as { message?: string }).message ?? '';
    return status === 401 || status === 403 || code === '401' || code === '403' || /unauthorized|not authorized/i.test(message);
  };

  const isRpcNotFound = (e: unknown) => {
    if (!e || typeof e !== 'object') return false;
    const status = (e as { status?: number }).status;
    const message = (e as { message?: string }).message ?? '';
    return status === 404 || /Could not find the function/i.test(message);
  };

  const fetchProjectsAndProfiles = async (): Promise<{ projects: ProjectRow[]; profiles: ProfileRow[] }> => {
    // 1) Mode PIN forcé
    if (shouldUsePinMode()) {
      const pin = getImportPinOrThrow();
      const [{ data: projectsData, error: projectsErr }, { data: profilesData, error: profilesErr }] = await Promise.all([
        supabase.rpc('pin_list_projects', { p_pin: pin }),
        supabase.rpc('pin_list_profiles', { p_pin: pin }),
      ]);
      if (projectsErr) {
        throw projectsErr;
      }
      if (profilesErr) {
        throw profilesErr;
      }
      return {
        projects: (projectsData ?? []) as unknown as ProjectRow[],
        profiles: (profilesData ?? []) as unknown as ProfileRow[],
      };
    }

    // 2) Mode Supabase “classique”
    try {
      const [{ data: projectsData, error: projectsErr }, { data: profilesData, error: profilesErr }] = await Promise.all([
        supabase.from('projects').select('id,name,description,address,status'),
        supabase.from('profiles').select('id,first_name,last_name,email'),
      ]);
      if (projectsErr) throw projectsErr;
      if (profilesErr) throw profilesErr;
      return {
        projects: (projectsData ?? []) as unknown as ProjectRow[],
        profiles: (profilesData ?? []) as unknown as ProfileRow[],
      };
    } catch (e: unknown) {
      // 3) Fallback automatique: session absente/expirée => RPC PIN si possible
      if (isUnauthorized(e)) {
        const pin = getImportPinOrThrow();
        const [{ data: projectsData, error: projectsErr }, { data: profilesData, error: profilesErr }] = await Promise.all([
          supabase.rpc('pin_list_projects', { p_pin: pin }),
          supabase.rpc('pin_list_profiles', { p_pin: pin }),
        ]);
        if (projectsErr) throw projectsErr;
        if (profilesErr) throw profilesErr;
        return {
          projects: (projectsData ?? []) as unknown as ProjectRow[],
          profiles: (profilesData ?? []) as unknown as ProfileRow[],
        };
      }
      throw e;
    }
  };

  const refreshProjects = async (): Promise<ProjectRow[]> => {
    if (shouldUsePinMode()) {
      const pin = getImportPinOrThrow();
      const { data, error } = await supabase.rpc('pin_list_projects', { p_pin: pin });
      if (error) throw error;
      return (data ?? []) as unknown as ProjectRow[];
    }
    const { data, error } = await supabase.from('projects').select('id,name,description,address,status');
    if (error) throw error;
    return (data ?? []) as unknown as ProjectRow[];
  };

  const insertProjects = async (projects: Array<Record<string, unknown>>) => {
    if (shouldUsePinMode()) {
      const pin = getImportPinOrThrow();
      const { data, error } = await supabase.rpc('pin_insert_projects', { p_pin: pin, p_projects: projects });
      if (error) throw error;
      return getInsertedCountFromRpc(data);
    }
    try {
      const { data, error } = await supabase.from('projects').insert(projects).select('id');
      if (error) throw error;
      return data?.length ?? 0;
    } catch (e: unknown) {
      if (isUnauthorized(e)) {
        const pin = getImportPinOrThrow();
        const { data, error } = await supabase.rpc('pin_insert_projects', { p_pin: pin, p_projects: projects });
        if (error) throw error;
        return getInsertedCountFromRpc(data);
      }
      throw e;
    }
  };

  const insertTimeEntries = async (entries: Array<Record<string, unknown>>) => {
    if (shouldUsePinMode()) {
      const pin = getImportPinOrThrow();
      const { data, error } = await supabase.rpc('pin_insert_time_entries', { p_pin: pin, p_entries: entries });
      if (error) throw error;
      return getInsertedCountFromRpc(data);
    }
    try {
      const { data, error } = await supabase.rpc('admin_insert_time_entries', { p_entries: entries });
      if (error) throw error;
      return getInsertedCountFromRpc(data);
    } catch (e: unknown) {
      if (isUnauthorized(e)) {
        const pin = getImportPinOrThrow();
        const { data, error } = await supabase.rpc('pin_insert_time_entries', { p_pin: pin, p_entries: entries });
        if (error) throw error;
        return getInsertedCountFromRpc(data);
      }
      throw e;
    }
  };

  const projectOptions = useMemo(() => {
    return [...existingProjects]
      .filter((p) => p.id && p.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ id: p.id as string, label: p.name }));
  }, [existingProjects]);

  const profileOptions = useMemo(() => {
    return [...profiles]
      .sort((a, b) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.localeCompare(`${b.first_name ?? ''} ${b.last_name ?? ''}`))
      .map((p) => ({
        id: p.id,
        label: `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() + (p.email ? ` (${p.email})` : ''),
      }));
  }, [profiles]);

  const maps = useMemo(
    () => buildMaps(existingProjects, projectFlowRows, profiles, employeeRegistryRows),
    [employeeRegistryRows, existingProjects, profiles, projectFlowRows],
  );

  const resolveProjectId = (projetRaw: string | undefined | null): string | null => {
    const raw = normalizeValue(projetRaw ?? '');
    if (!raw) return null;
    // 1) Projet = pf_id taggé dans projects.description
    const byPf = maps.existingByPfId.get(raw);
    if (byPf) return byPf;
    // 2) Projet = nom direct
    const byName = maps.existingByName.get(raw);
    if (byName) return byName;
    // 3) Projet = id_projet (ProjectFlow) -> matcher son nom vers un projet existant
    const pfRow = maps.pfById.get(raw);
    if (pfRow?.nom_du_projet) {
      const pfName = normalizeValue(pfRow.nom_du_projet);
      const byPfName = maps.existingByName.get(pfName);
      if (byPfName) return byPfName;
    }
    return null;
  };

  const resolveUserId = (employeeRaw: string | undefined | null): string | null => {
    const full = normalizeValue(employeeRaw ?? '');
    if (!full) return null;
    const direct = maps.profileByFullName.get(full);
    if (direct) return direct;
    const email = maps.registryByFullNameToEmail.get(full);
    if (email) {
      const byEmail = maps.profileByEmail.get(email);
      if (byEmail) return byEmail;
    }
    return null;
  };

  const recomputeUnknowns = (projectsOverride?: ProjectRow[]) => {
    const localMaps = buildMaps(projectsOverride ?? existingProjects, projectFlowRows, profiles, employeeRegistryRows);
    const unknownProjectsCount = new Map<string, number>();
    const unknownEmployeesCount = new Map<string, number>();

    for (const row of timesheetRows) {
      const pRaw = String(row.projet ?? '').trim();
      const uRaw = String(row.employe ?? '').trim();

      const pKey = normalizeValue(pRaw);
      const projectId =
        (pKey && localMaps.existingByPfId.get(pKey)) ||
        (pKey && localMaps.existingByName.get(pKey)) ||
        (pKey &&
          (() => {
            const pfRow = localMaps.pfById.get(pKey);
            if (!pfRow?.nom_du_projet) return null;
            const pfName = normalizeValue(pfRow.nom_du_projet);
            return localMaps.existingByName.get(pfName) ?? null;
          })());
      if (!projectId && pRaw) unknownProjectsCount.set(pRaw, (unknownProjectsCount.get(pRaw) ?? 0) + 1);

      const uKey = normalizeValue(uRaw);
      const userId =
        (uKey && localMaps.profileByFullName.get(uKey)) ||
        (uKey &&
          (() => {
            const email = localMaps.registryByFullNameToEmail.get(uKey);
            if (!email) return null;
            return localMaps.profileByEmail.get(email) ?? null;
          })());
      if (!userId && uRaw) unknownEmployeesCount.set(uRaw, (unknownEmployeesCount.get(uRaw) ?? 0) + 1);
    }

    setUnknownProjectValues(
      [...unknownProjectsCount.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({ value, count })),
    );
    setUnknownEmployeeValues(
      [...unknownEmployeesCount.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({ value, count })),
    );
  };

  const analyze = async () => {
    if (!timesheetFile || !projectFlowFile || !employeeRegistryFile) {
      toast({ title: 'Veuillez sélectionner les 3 fichiers CSV', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const [pfRowsRaw, empRowsRaw, tsRowsRaw] = await Promise.all([
        parseCsvFile<ProjectFlowRow>(projectFlowFile),
        parseCsvFile<EmployeeRegistryRow>(employeeRegistryFile),
        parseCsvFile<TimesheetRow>(timesheetFile),
      ]);
      const { projects: localProjects, profiles: localProfiles } = await fetchProjectsAndProfiles();

      const localMaps = buildMaps(localProjects, pfRowsRaw, localProfiles, empRowsRaw);

      // Missing projects: not found by pf_id marker AND not found by name
      const missing: ProjectFlowRow[] = [];
      const conflicts: ProjectFlowRow[] = [];
      for (const r of pfRowsRaw) {
        const pfId = normalizeValue(r.id_projet ?? '');
        const name = normalizeValue(r.nom_du_projet ?? '');
        if (!pfId && !name) continue;

        const foundByPf = pfId ? localMaps.existingByPfId.get(pfId) : undefined;
        const foundByName = name ? localMaps.existingByName.get(name) : undefined;

        if (foundByPf) continue;
        if (foundByName) {
          // name exists but pf_id not tagged => conflict to review (avoid creating a duplicate)
          conflicts.push(r);
          continue;
        }
        missing.push(r);
      }

      // Unknown project values in timesheet
      const unknownProjectsCount = new Map<string, number>();
      const unknownEmployeesCount = new Map<string, number>();

      for (const row of tsRowsRaw) {
        const pRaw = String(row.projet ?? '').trim();
        const uRaw = String(row.employe ?? '').trim();

        const pKey = normalizeValue(pRaw);
        const projectId =
          (pKey && localMaps.existingByPfId.get(pKey)) ||
          (pKey && localMaps.existingByName.get(pKey)) ||
          (pKey &&
            (() => {
              const pfRow = localMaps.pfById.get(pKey);
              if (!pfRow?.nom_du_projet) return null;
              const pfName = normalizeValue(pfRow.nom_du_projet);
              return localMaps.existingByName.get(pfName) ?? null;
            })());
        if (!projectId && pRaw) unknownProjectsCount.set(pRaw, (unknownProjectsCount.get(pRaw) ?? 0) + 1);

        const uKey = normalizeValue(uRaw);
        const userId =
          (uKey && localMaps.profileByFullName.get(uKey)) ||
          (uKey &&
            (() => {
              const email = localMaps.registryByFullNameToEmail.get(uKey);
              if (!email) return null;
              return localMaps.profileByEmail.get(email) ?? null;
            })());
        if (!userId && uRaw) unknownEmployeesCount.set(uRaw, (unknownEmployeesCount.get(uRaw) ?? 0) + 1);
      }

      // Set state only after computations (avoid stale maps)
      setExistingProjects(localProjects);
      setProfiles(localProfiles);
      setProjectFlowRows(pfRowsRaw);
      setEmployeeRegistryRows(empRowsRaw);
      setTimesheetRows(tsRowsRaw);

      setMissingProjects(missing);
      setConflictingProjects(conflicts);

      setUnknownProjectValues(
        [...unknownProjectsCount.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([value, count]) => ({ value, count })),
      );
      setUnknownEmployeeValues(
        [...unknownEmployeesCount.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([value, count]) => ({ value, count })),
      );

      setAnalysisDone(true);
      toast({
        title: 'Analyse terminée',
        description: `${pfRowsRaw.length} projets PF, ${tsRowsRaw.length} lignes de temps, ${missing.length} projets manquants`,
      });
    } catch (e: unknown) {
      if (isRpcNotFound(e)) {
        toast({
          title: 'RPC manquante côté Supabase',
          description:
            'Les fonctions RPC PIN ne sont pas encore déployées. Applique la migration 20260115131000-pin-import-projets-shifts.sql sur Supabase, puis réessaie.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Erreur analyse', description: getErrorMessage(e), variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const createMissingProjectsInDb = async () => {
    if (!analysisDone) return;
    if (missingProjects.length === 0) {
      toast({ title: 'Aucun projet à créer' });
      return;
    }
    setLoading(true);
    try {
      const toInsert = missingProjects
        .map((r) => {
          const pfId = String(r.id_projet ?? '').trim();
          const name = String(r.nom_du_projet ?? '').trim();
          const address = (String(r.adresse_specifique ?? '').trim() || String(r.adresse_chantier ?? '').trim()) || null;
          const estimated = parseNumberComma(r.heures_planiifiees) ?? null;
          const client = String(r.numero_de_client ?? '').trim() || null;
          return {
            name,
            address,
            client_name: client,
            estimated_hours: estimated ? Math.round(estimated) : null,
            status: mapProjectStatus(r.statut),
            description: `Import ProjectFlow pf_id=${pfId}`.trim(),
          };
        })
        .filter((p) => p.name);
      let inserted = 0;
      const chunkSize = 200;
      for (let i = 0; i < toInsert.length; i += chunkSize) {
        const chunk = toInsert.slice(i, i + chunkSize);
        inserted += await insertProjects(chunk);
      }

      const refreshed = await refreshProjects();
      setExistingProjects(refreshed);
      recomputeUnknowns(refreshed);

      setMissingProjects([]);
      toast({ title: 'Projets créés', description: `${inserted} projet(s) ajouté(s)` });
    } catch (e: unknown) {
      if (isRpcNotFound(e)) {
        toast({
          title: 'RPC manquante côté Supabase',
          description:
            'Les fonctions RPC PIN ne sont pas encore déployées. Applique la migration 20260115131000-pin-import-projets-shifts.sql sur Supabase, puis réessaie.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Erreur création projets', description: getErrorMessage(e), variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUnknownProjectMapping = (value: string, mappedId: string) => {
    setUnknownProjectValues((prev) => prev.map((u) => (u.value === value ? { ...u, mappedId } : u)));
  };
  const updateUnknownEmployeeMapping = (value: string, mappedId: string) => {
    setUnknownEmployeeValues((prev) => prev.map((u) => (u.value === value ? { ...u, mappedId } : u)));
  };

  const buildManualMaps = () => {
    const manualProject = new Map<string, string>();
    for (const u of unknownProjectValues) {
      if (u.mappedId) manualProject.set(normalizeValue(u.value), u.mappedId);
    }
    const manualUser = new Map<string, string>();
    for (const u of unknownEmployeeValues) {
      if (u.mappedId) manualUser.set(normalizeValue(u.value), u.mappedId);
    }
    return { manualProject, manualUser };
  };

  const resolveProjectIdWithManual = (projetRaw: string | undefined | null, manualProject: Map<string, string>): string | null => {
    const raw = normalizeValue(projetRaw ?? '');
    if (!raw) return null;
    const viaManual = manualProject.get(raw);
    if (viaManual) return viaManual;
    // Try PF id marker projects
    const byPf = maps.existingByPfId.get(raw);
    if (byPf) return byPf;
    const byName = maps.existingByName.get(raw);
    if (byName) return byName;
    // Projet = id_projet (ProjectFlow) -> matcher son nom vers un projet existant
    const pfRow = maps.pfById.get(raw);
    if (pfRow?.nom_du_projet) {
      const pfName = normalizeValue(pfRow.nom_du_projet);
      const byPfName = maps.existingByName.get(pfName);
      if (byPfName) return byPfName;
    }
    return null;
  };

  const resolveUserIdWithManual = (employeeRaw: string | undefined | null, manualUser: Map<string, string>): string | null => {
    const raw = normalizeValue(employeeRaw ?? '');
    if (!raw) return null;
    const viaManual = manualUser.get(raw);
    if (viaManual) return viaManual;
    return resolveUserId(employeeRaw);
  };

  const importTimeEntries = async () => {
    if (!analysisDone) return;
    setLoading(true);
    try {
      const { manualProject, manualUser } = buildManualMaps();

      const entries: Array<Record<string, unknown>> = [];
      const skipped: Array<{ reason: string; row: TimesheetRow }> = [];

      for (const row of timesheetRows) {
        const projetRaw = row.projet ?? '';
        const employeRaw = row.employe ?? '';

        const projectId = resolveProjectIdWithManual(projetRaw, manualProject);
        const userId = resolveUserIdWithManual(employeRaw, manualUser);

        if (!projectId || !userId) {
          if (skipUnmapped) {
            skipped.push({ reason: !projectId ? 'projet_non_resolu' : 'employe_non_resolu', row });
            continue;
          }
          throw new Error(`Mapping manquant: projet="${projetRaw}" employé="${employeRaw}"`);
        }

        const startLocal = buildDateTimeLocal(row.date ?? '', row.heure_debut ?? '');
        let endLocal = buildDateTimeLocal(row.date ?? '', row.heure_fin ?? '');
        if (!startLocal) {
          skipped.push({ reason: 'start_time_invalide', row });
          continue;
        }
        // Si le shift traverse minuit (ex: 22:00 → 02:00), on suppose fin le lendemain.
        if (endLocal && endLocal.getTime() <= startLocal.getTime()) {
          endLocal = new Date(endLocal);
          endLocal.setDate(endLocal.getDate() + 1);
        }

        const isBillable = parseBooleanLike(row.facturable);
        const isInvoiced = parseBooleanLike(row.qbe);
        const hourlyRate = parseNumberComma(row.taux_horaire);

        const main = String(row.tache_principale ?? '').trim();
        const details = String(row.details ?? '').trim();
        const description = [main, details].filter(Boolean).join(' — ') || null;

        entries.push({
          user_id: userId,
          project_id: projectId,
          start_time: startLocal.toISOString(),
          end_time: endLocal ? endLocal.toISOString() : null,
          description,
          is_billable: isBillable ?? true,
          hourly_rate: hourlyRate,
          is_invoiced: isInvoiced ?? false,
        });
      }

      if (entries.length === 0) {
        toast({ title: 'Rien à importer', description: 'Aucune entrée valide après mapping/filtrage' });
        return;
      }

      const chunkSize = 500;
      let inserted = 0;
      for (let i = 0; i < entries.length; i += chunkSize) {
        const chunk = entries.slice(i, i + chunkSize);
        inserted += await insertTimeEntries(chunk);
      }

      toast({
        title: 'Import terminé',
        description: `${inserted} shift(s) importé(s)${skipped.length ? `, ${skipped.length} ignoré(s)` : ''}`,
      });
    } catch (e: unknown) {
      if (isRpcNotFound(e)) {
        toast({
          title: 'RPC manquante côté Supabase',
          description:
            'Les fonctions RPC PIN ne sont pas encore déployées. Applique la migration 20260115131000-pin-import-projets-shifts.sql sur Supabase, puis réessaie.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Erreur import', description: getErrorMessage(e), variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Garde d'accès (après tous les hooks)
  if (pinLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  if (!pinUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isPinManager()) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle>Accès restreint</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Cette page est réservée aux rôles manager/contremaître/admin.</p>
            <Button variant="secondary" onClick={() => navigate('/dashboard')}>
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileUp className="h-5 w-5" /> Import Projets + Shifts
            </h1>
            <p className="text-sm text-muted-foreground">
              Analyse les CSV, crée les projets manquants, puis importe les shifts dans <code>time_entries</code>.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>1) Fichiers CSV</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Feuille de temps (nettoyée)</Label>
                <Input type="file" accept=".csv,text/csv" onChange={(e) => setTimesheetFile(e.target.files?.[0] ?? null)} />
              </div>
              <div className="space-y-2">
                <Label>Projets (ProjectFlow)</Label>
                <Input type="file" accept=".csv,text/csv" onChange={(e) => setProjectFlowFile(e.target.files?.[0] ?? null)} />
              </div>
              <div className="space-y-2">
                <Label>Employés (registre)</Label>
                <Input type="file" accept=".csv,text/csv" onChange={(e) => setEmployeeRegistryFile(e.target.files?.[0] ?? null)} />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <Button onClick={analyze} disabled={loading || !timesheetFile || !projectFlowFile || !employeeRegistryFile}>
                {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Lancer l’analyse
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <Checkbox id="skipUnmapped" checked={skipUnmapped} onCheckedChange={(v) => setSkipUnmapped(Boolean(v))} />
                <Label htmlFor="skipUnmapped">Ignorer les lignes avec projet/employé non résolu</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {analysisDone && (
          <Card>
            <CardHeader>
              <CardTitle>2) Projets manquants</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Projets ProjectFlow absents de la table <code>projects</code> (matching par <code>pf_id</code> puis par nom).
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <Button onClick={createMissingProjectsInDb} disabled={loading || missingProjects.length === 0}>
                  Créer {missingProjects.length} projet(s)
                </Button>
              </div>

              {conflictingProjects.length > 0 && (
                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium mb-2">Conflits détectés (nom existe déjà)</div>
                  <div className="text-muted-foreground">
                    On évite de créer un doublon. Si besoin, renomme ou ajoute un tag <code>pf_id=...</code> dans la description du projet existant.
                  </div>
                </div>
              )}

              <Separator />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>id_projet</TableHead>
                    <TableHead>nom</TableHead>
                    <TableHead>statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missingProjects.slice(0, 20).map((p, idx) => (
                    <TableRow key={`${p.id_projet}-${idx}`}>
                      <TableCell className="font-mono text-xs">{p.id_projet}</TableCell>
                      <TableCell>{p.nom_du_projet}</TableCell>
                      <TableCell>{p.statut}</TableCell>
                    </TableRow>
                  ))}
                  {missingProjects.length > 20 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-sm text-muted-foreground">
                        … {missingProjects.length - 20} autre(s) projet(s)
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {analysisDone && (
          <Card>
            <CardHeader>
              <CardTitle>3) Mapping manuel (optionnel)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-sm text-muted-foreground">
                Si certaines valeurs <code>Projet</code> / <code>Employé</code> du fichier temps ne correspondent pas automatiquement, tu peux les
                apparier ici.
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="font-medium">Projets non résolus ({unknownProjectValues.length})</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Valeur</TableHead>
                        <TableHead className="w-[140px]">Occ.</TableHead>
                        <TableHead>Associer à</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unknownProjectValues.slice(0, 15).map((u) => (
                        <TableRow key={u.value}>
                          <TableCell className="max-w-[220px] truncate" title={u.value}>
                            {u.value}
                          </TableCell>
                          <TableCell>{u.count}</TableCell>
                          <TableCell>
                            <Select value={u.mappedId} onValueChange={(v) => updateUnknownProjectMapping(u.value, v)}>
                              <SelectTrigger className="w-[260px]">
                                <SelectValue placeholder="Choisir..." />
                              </SelectTrigger>
                              <SelectContent>
                                {projectOptions.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                      {unknownProjectValues.length > 15 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-sm text-muted-foreground">
                            … {unknownProjectValues.length - 15} autre(s)
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="space-y-3">
                  <div className="font-medium">Employés non résolus ({unknownEmployeeValues.length})</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Valeur</TableHead>
                        <TableHead className="w-[140px]">Occ.</TableHead>
                        <TableHead>Associer à</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unknownEmployeeValues.slice(0, 15).map((u) => (
                        <TableRow key={u.value}>
                          <TableCell className="max-w-[220px] truncate" title={u.value}>
                            {u.value}
                          </TableCell>
                          <TableCell>{u.count}</TableCell>
                          <TableCell>
                            <Select value={u.mappedId} onValueChange={(v) => updateUnknownEmployeeMapping(u.value, v)}>
                              <SelectTrigger className="w-[260px]">
                                <SelectValue placeholder="Choisir..." />
                              </SelectTrigger>
                              <SelectContent>
                                {profileOptions.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                      {unknownEmployeeValues.length > 15 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-sm text-muted-foreground">
                            … {unknownEmployeeValues.length - 15} autre(s)
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {analysisDone && (
          <Card>
            <CardHeader>
              <CardTitle>4) Import des shifts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Les shifts sont insérés via la RPC <code>admin_insert_time_entries</code> (voir migration SQL).
              </div>
              <Button onClick={importTimeEntries} disabled={loading}>
                Importer les shifts
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

